import { DragEvent, useState, useCallback, useRef } from 'react'
import { css } from '@emotion/core'

type Props<T> = {
  list: Array<T>
  onChange: (newList: Array<T>) => void
  direction?: 'vertical' | 'horizontal'
  key?: string
}
type DragPoint = { clientX: number; clientY: number }
const DRAG_THRESHOLD_PX = 10
const LONG_PRESS_MS = 300

export function useDraggableDom<T>({ list, onChange, direction = 'vertical', key = 'id' }: Props<T>) {
  const [draggingList, setDraggingList] = useState<(typeof list)[number] | undefined>()
  const [draggedOverId, setDraggedOverId] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const touchStartPos = useRef<{ x: number; y: number } | null>(null)
  const draggingElementRef = useRef<HTMLElement | null>(null)
  const touchGhostRef = useRef<HTMLElement | null>(null)
  const touchGhostOffsetRef = useRef<{ x: number; y: number } | null>(null)
  const touchLongPressTimerRef = useRef<number | null>(null)
  const touchPendingTargetRef = useRef<HTMLElement | null>(null)
  const touchScrollBlockHandlerRef = useRef<((event: TouchEvent) => void) | null>(null)

  // TouchEvent / MouseEvent から画面座標を取り出す共通ヘルパー
  const getEventPoint = (event: TouchEvent | MouseEvent) => {
    if (event instanceof TouchEvent && event.changedTouches.length > 0) {
      const touch = event.changedTouches[0]
      return { clientX: touch.clientX, clientY: touch.clientY }
    }
    if (event instanceof TouchEvent && event.touches.length > 0) {
      const touch = event.touches[0]
      return { clientX: touch.clientX, clientY: touch.clientY }
    }
    if (event instanceof MouseEvent) {
      return { clientX: event.clientX, clientY: event.clientY }
    }
    return { clientX: 0, clientY: 0 }
  }

  // ドラッグ中の見た目制御（元要素）
  const applyDraggingVisual = (target: HTMLElement, isMoving = false) => {
    target.style.opacity = isMoving ? '0.85' : '0.9'
    target.style.transform = isMoving ? 'scale(1.02)' : 'scale(1.01)'
    target.style.zIndex = '1000'
    target.style.position = 'relative'
    target.style.transition = 'transform 0.15s ease, opacity 0.15s ease'
  }

  const resetDraggingVisual = (target: HTMLElement | null) => {
    if (!target) return
    target.style.opacity = ''
    target.style.transform = ''
    target.style.zIndex = ''
    target.style.position = ''
    target.style.transition = ''
  }

  // タッチ時にだけ表示する追従ゴースト（半透明プレビュー）
  const createTouchGhost = (target: HTMLElement, touch: DragPoint) => {
    if (touchGhostRef.current) return
    const rect = target.getBoundingClientRect()
    const ghost = target.cloneNode(true) as HTMLElement
    ghost.removeAttribute('id')
    ghost.style.position = 'fixed'
    ghost.style.top = '0'
    ghost.style.left = '0'
    ghost.style.margin = '0'
    ghost.style.width = `${rect.width}px`
    ghost.style.height = `${rect.height}px`
    ghost.style.pointerEvents = 'none'
    ghost.style.opacity = '0.6'
    ghost.style.zIndex = '2000'
    ghost.style.transform = `translate3d(${rect.left}px, ${rect.top}px, 0)`
    ghost.style.transition = 'none'
    ghost.style.willChange = 'transform'
    target.parentElement?.appendChild(ghost)
    touchGhostRef.current = ghost
    touchGhostOffsetRef.current = {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    }
  }

  const moveTouchGhost = (touch: DragPoint) => {
    if (!touchGhostRef.current || !touchGhostOffsetRef.current) return
    const x = touch.clientX - touchGhostOffsetRef.current.x
    const y = touch.clientY - touchGhostOffsetRef.current.y
    touchGhostRef.current.style.transform = `translate3d(${x}px, ${y}px, 0)`
  }

  const removeTouchGhost = () => {
    touchGhostRef.current?.remove()
    touchGhostRef.current = null
    touchGhostOffsetRef.current = null
  }

  // 長押し判定タイマーの管理
  const clearTouchLongPressTimer = () => {
    if (touchLongPressTimerRef.current !== null) {
      window.clearTimeout(touchLongPressTimerRef.current)
      touchLongPressTimerRef.current = null
    }
  }

  // ドラッグ中のみ、ネイティブスクロールを抑止する（passive: false）
  const enableTouchScrollBlock = () => {
    if (touchScrollBlockHandlerRef.current) return
    const handler = (event: TouchEvent) => {
      event.preventDefault()
    }
    document.addEventListener('touchmove', handler, { passive: false })
    touchScrollBlockHandlerRef.current = handler
  }

  const disableTouchScrollBlock = () => {
    const handler = touchScrollBlockHandlerRef.current
    if (!handler) return
    document.removeEventListener('touchmove', handler)
    touchScrollBlockHandlerRef.current = null
  }

  // ドラッグ操作を終了する共通後処理
  const resetDraggingState = () => {
    clearTouchLongPressTimer()
    touchPendingTargetRef.current = null
    disableTouchScrollBlock()
    setDraggingList(undefined)
    setIsDragging(false)
    setDraggedOverId(null)
    touchStartPos.current = null
    resetDraggingVisual(draggingElementRef.current)
    draggingElementRef.current = null
    removeTouchGhost()
  }

  // 指/マウス位置が要素の前半・後半どちら側かを判定
  const isDropToAfterByPoint = (element: HTMLElement, point: DragPoint): boolean => {
    const { top, bottom, left, right } = element.getBoundingClientRect()
    if (direction === 'vertical') {
      return Math.abs(top - point.clientY) > Math.abs(bottom - point.clientY)
    } else {
      return Math.abs(left - point.clientX) > Math.abs(right - point.clientX)
    }
  }

  // HTML5 Drag and Drop API用のisDropToAfter（既存機能を維持）
  const isDropToAfter = (event: DragEvent<HTMLElement>): boolean => {
    return isDropToAfterByPoint(event.currentTarget, { clientX: event.clientX, clientY: event.clientY })
  }

  // タッチ/マウスイベント用のisDropToAfter
  const isDropToAfterForTouch = useCallback(
    (event: TouchEvent | MouseEvent, element: HTMLElement): boolean => {
      const { clientX, clientY } = getEventPoint(event)
      return isDropToAfterByPoint(element, { clientX, clientY })
    },
    [direction],
  )

  const getDropTargetIdFromEvent = useCallback(
    (event: TouchEvent | MouseEvent): string | null => {
      const { clientX, clientY } = getEventPoint(event)
      const element = document.elementFromPoint(clientX, clientY)
      const rowElement = element?.closest('[id]') as HTMLElement | null
      if (!rowElement?.id) return null
      return list.some(item => item[key].toString() === rowElement.id) ? rowElement.id : null
    },
    [list, key],
  )

  const getDropTargetElementFromEvent = useCallback(
    (event: TouchEvent | MouseEvent, fallbackId?: string | null): HTMLElement | null => {
      const { clientX, clientY } = getEventPoint(event)
      const element = document.elementFromPoint(clientX, clientY)
      const rowElement = element?.closest('[id]') as HTMLElement | null
      if (rowElement?.id && list.some(item => item[key].toString() === rowElement.id)) {
        return rowElement
      }
      if (!fallbackId) return null
      const fallbackElement = document.getElementById(fallbackId)
      if (!fallbackElement) return null
      return list.some(item => item[key].toString() === fallbackElement.id) ? fallbackElement : null
    },
    [list, key],
  )

  // HTML5 Drag and Drop API用のハンドラー（既存機能）
  const startDragFromTarget = useCallback(
    (target: HTMLElement) => {
      const item = list.find(listItem => listItem[key].toString() === target.id)
      if (!item) return false
      draggingElementRef.current = target
      setDraggingList(item)
      setDraggedOverId(target.id)
      setIsDragging(true)
      applyDraggingVisual(target)
      enableTouchScrollBlock()
      return true
    },
    [list, key, enableTouchScrollBlock],
  )

  // dropOnListId と前後判定を使って実際に配列を並び替える
  const commitDrop = useCallback(
    (dropOnListId: string | null | undefined, isAfter: boolean) => {
      if (!draggingList || !dropOnListId || dropOnListId === draggingList[key].toString()) return

      const filteredDragList = list.filter(item => item[key] !== draggingList[key])
      const dropListIndex = filteredDragList.findIndex(item => item[key].toString() === dropOnListId)
      if (dropListIndex === -1) return

      filteredDragList.splice(isAfter ? dropListIndex + 1 : dropListIndex, 0, draggingList)
      onChange([...filteredDragList])
    },
    [draggingList, key, list, onChange],
  )

  const dragStart = (event: DragEvent<HTMLElement>) => {
    startDragFromTarget(event.currentTarget)
  }

  const dragOver = (event: DragEvent<HTMLElement>) => {
    event.preventDefault()
  }

  const dragDrop = (event: DragEvent<HTMLElement>, isManual?: boolean) => {
    event.preventDefault()
    const dropOnListId = event.currentTarget.id

    if (isManual && draggingList) {
      return {
        from: draggingList[key],
        to: dropOnListId,
        isDropToAfter: isDropToAfter(event),
      }
    }

    commitDrop(dropOnListId, isDropToAfter(event))
    resetDraggingState()
  }

  const dragEnd = () => {
    resetDraggingState()
  }

  // data-drag-handle がある場合は、そのハンドル領域だけをドラッグ開始対象にする
  const canStartDragFromTouch = useCallback((target: HTMLElement, eventTarget: EventTarget | null) => {
    const dragIcon = target.querySelector('[data-drag-handle]')
    if (!dragIcon) return true
    return eventTarget instanceof Node && dragIcon.contains(eventTarget)
  }, [])

  // タッチ開始: 長押しタイマーを開始（長押し成立でドラッグ開始）
  const handleTouchStart = useCallback(
    (event: React.TouchEvent<HTMLElement>) => {
      const target = event.currentTarget
      if (!target.draggable) return
      if (!canStartDragFromTouch(target, event.target)) return

      const touch = event.touches[0]
      if (!touch) return
      touchStartPos.current = { x: touch.clientX, y: touch.clientY }

      clearTouchLongPressTimer()
      touchPendingTargetRef.current = target
      touchLongPressTimerRef.current = window.setTimeout(() => {
        const pendingTarget = touchPendingTargetRef.current
        touchLongPressTimerRef.current = null
        if (!pendingTarget || isDragging) return
        startDragFromTarget(pendingTarget)
        touchPendingTargetRef.current = null
      }, LONG_PRESS_MS)
    },
    [isDragging, startDragFromTarget, canStartDragFromTouch],
  )

  // タッチ移動: 長押し前はスクロール優先、ドラッグ開始後はゴースト追従
  const handleTouchMove = useCallback(
    (event: React.TouchEvent<HTMLElement>) => {
      if (!touchStartPos.current) return
      // 1本指でタッチしている前提。その場所の座標を取得する
      const touch = event.touches[0]
      if (!touch) return
      // タッチした場所と、タッチ開始した場所の差分を取得する
      const deltaX = Math.abs(touch.clientX - touchStartPos.current.x)
      const deltaY = Math.abs(touch.clientY - touchStartPos.current.y)

      if (!isDragging) {
        // 長押し前の移動はスクロールとして扱う
        if (deltaX > DRAG_THRESHOLD_PX || deltaY > DRAG_THRESHOLD_PX) {
          clearTouchLongPressTimer()
          touchPendingTargetRef.current = null
        }
        return
      }

      event.preventDefault()
      const point: DragPoint = { clientX: touch.clientX, clientY: touch.clientY }
      if (!touchGhostRef.current) {
        createTouchGhost(event.currentTarget, point)
      }
      moveTouchGhost(point)
      const hoveredId = getDropTargetIdFromEvent(event.nativeEvent)
      setDraggedOverId(hoveredId)

      // ドラッグ中の視覚的フィードバック
      const target = event.currentTarget
      applyDraggingVisual(target, true)
    },
    [isDragging, getDropTargetIdFromEvent],
  )

  // タッチ終了: ドロップ位置を確定して並び替えを確定
  const handleTouchEnd = useCallback(
    (event: React.TouchEvent<HTMLElement>) => {
      clearTouchLongPressTimer()
      touchPendingTargetRef.current = null
      if (!isDragging) {
        resetDraggingState()
        return
      }

      const dropOnListId = draggedOverId ?? getDropTargetIdFromEvent(event.nativeEvent)
      const dropTargetElement = getDropTargetElementFromEvent(event.nativeEvent, dropOnListId)

      if (dropTargetElement) {
        const isAfter = isDropToAfterForTouch(event.nativeEvent, dropTargetElement)
        commitDrop(dropOnListId, isAfter)
      }

      resetDraggingState()
    },
    [isDragging, commitDrop, isDropToAfterForTouch, getDropTargetIdFromEvent, getDropTargetElementFromEvent, draggedOverId],
  )

  return {
    // HTML5 Drag and Drop API用（既存機能、後方互換性のため維持）
    dragStart,
    dragOver,
    dragDrop,
    dragEnd,
    isDropToAfter,
    // タッチ/マウスイベント用（新機能）
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    isDragging,
    draggedOverId,
  }
}

export const draggableIconStyle = css({
  '&[draggable="true"]': {
    cursor: 'pointer',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
    position: 'relative',
    ':hover': {
      ':before': {
        content: '""',
        width: 30,
        height: 30,
        backgroundSize: 30,
        backgroundImage: `url(${require('@/static/images/icon_list.svg')})`,
        position: 'absolute',
        left: 16,
      },
    },
  },
})
